import type { DateRange } from "@smartsuite/types";
import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import * as yup from "yup";

export const defaultDateRangeValue = {
  from_date: {
    date: "",
    include_time: false,
  },
  to_date: {
    date: "",
    include_time: false,
  },
};

// A profile's date_format is stored in moment syntax. DateRangeCalendar hands it to date-fns via
// its own convertToDateFnsFormat, which rewrites the doubled tokens (DD -> dd, YYYY -> yyyy) but
// not the single-letter ones — so a format like "D-M-yyyy" (e.g. French (Canada)) reaches date-fns
// with D still meaning day-of-year, and date-fns throws RangeError: Use `d` instead of `D`. That
// throw killed the whole picker.
//
// Lower-casing whole runs of D and Y is the same mapping react-ui intends, just complete: moment's
// D/DD (day of month) and Y/YY/YYYY (year) map 1:1 onto date-fns d/dd and y/yy/yyyy, and M tokens
// are identical in both. react-ui's converter then finds nothing left to rewrite.
//
// The real fix belongs in @smartsuite/react-ui (every consumer — grid and record view included —
// has the same crash); this keeps forms working until that lands.
export const toDateFnsDateFormat = (dateFormat: string): string =>
  dateFormat
    .replace(/D+/g, (run) => "d".repeat(run.length))
    .replace(/Y+/g, (run) => "y".repeat(run.length));

// Parse one prefill boundary ("YYYY-MM-DD" or a full ISO datetime) into the
// value shape the control renders. Returns a string date (NOT a Date object):
// pinDateOnlyToUtcMidnight operates on the string, so a Date here crashes the
// field. A value carrying a time is kept as full ISO with include_time true; a
// date-only value stays "YYYY-MM-DD" (include_time false) — the control pins it
// to UTC midnight before the picker so the calendar highlights the right day.
// Anything unparseable returns null (invalid prefill is ignored).
export const prefillDateRangeBoundary = (
  value: string,
): { date: string; include_time: boolean } | null => {
  const trimmed = value.trim();

  if (!trimmed || !isValid(parseISO(trimmed))) {
    return null;
  }

  if (trimmed.includes("T") && trimmed.includes(":")) {
    return { date: parseISO(trimmed).toISOString(), include_time: true };
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }

  return { date: trimmed, include_time: false };
};

const pinDateOnly = (dateString: string | null): string | null =>
  dateString && !dateString.includes("T") ? `${dateString}T00:00:00.000Z` : dateString;

// The react-ui picker stores a date-only boundary as a local calendar day
// ("YYYY-MM-DD"). getFormattedDateRange -> dateValueToDate parses that as local
// midnight then converts to UTC, shifting the shown day by the user's offset
// (e.g. UTC+3 renders the previous day). Pin date-only boundaries to UTC midnight
// so the displayed day matches the picker in every timezone. Use for display only;
// the stored value stays a local calendar day so the picker highlights correctly.
// Boundaries that already carry a time are left untouched (shown in the user's tz).
export const pinDateOnlyToUtcMidnight = (range: DateRange): DateRange => ({
  from_date: { ...range.from_date, date: pinDateOnly(range.from_date.date) },
  to_date: { ...range.to_date, date: pinDateOnly(range.to_date.date) },
});

// getFormattedDateRange (fields-logic) renders the time but never a timezone. Append the
// timezone — resolved in the form's timezone so it matches the shown time — to any boundary
// that carries a time, mirroring how the Date field shows "(zzzz)". Date-only boundaries are
// left untouched (no time → no timezone). `formatted` is the display strings; `rawValue` is
// the source value whose ISO dates and include_time flags drive the timezone resolution.
export const appendTimezoneToFormattedRange = (
  formatted: DateRange,
  rawValue: DateRange,
  timezone: string,
): DateRange => {
  const withTimezone = (
    formattedBoundary: DateRange["from_date"],
    rawBoundary: DateRange["from_date"],
  ): DateRange["from_date"] => {
    if (!rawBoundary.include_time || !rawBoundary.date || !formattedBoundary.date) {
      return formattedBoundary;
    }

    // A checkpoint/API value may carry include_time with a broken date string —
    // formatInTimeZone throws on an invalid Date, which would crash the whole form.
    const parsedDate = new Date(rawBoundary.date);
    if (!isValid(parsedDate)) {
      return formattedBoundary;
    }

    const tz = formatInTimeZone(parsedDate, timezone, "OOOO");
    return { ...formattedBoundary, date: `${formattedBoundary.date} (${tz})` };
  };

  return {
    from_date: withTimezone(formatted.from_date, rawValue.from_date),
    to_date: withTimezone(formatted.to_date, rawValue.to_date),
  };
};

// The DateRangeCalendar emits null when the picker is closed with no selection, so the
// schema must be nullable — otherwise yup rejects null with "must be an object type".
// When required, null (and an empty to_date) fails with the required message instead.
export const createDateRangeSchema = (required: boolean, requiredMessage: string): yup.AnySchema =>
  yup
    .object()
    .nullable()
    .when({
      is: () => required,
      then: (schema) =>
        schema
          .shape({
            to_date: yup.object().shape({
              date: yup.string().required(requiredMessage),
              include_time: yup.boolean(),
            }),
            from_date: yup.object().shape({
              date: yup.string(),
              include_time: yup.boolean(),
            }),
          })
          .required(requiredMessage),
    });
